import { Component } from '@theme/component';

/**
 * MOXIE: tabs de sections/custom_carousel-tabs.liquid (fila #6 del home). Horizon no trae un
 * componente de tabs nativo (confirmado, ver docs/home-custom-todo.md) - patron WAI-ARIA Tabs
 * calcado de assets/custom-header-drawer.js (mismo framework Component<Refs>, mismo
 * on:click="/selectTab"), sin la logica de drill-down del drawer, que no aplica aca.
 *
 * Los botones de tab NO vienen renderizados desde Liquid (confirmado con debug real: `block.type`
 * devuelve el string literal "@theme" al introspeccionar bloques hermanos vía section.blocks, no
 * el tipo concreto del schema - limitacion de la plataforma) - este componente arma el tablist
 * leyendo los paneles ya renderizados (blocks/custom_carousel-tab.liquid, cada uno con
 * `data-tab-label`) y activa el primero al conectarse (mismo patron que
 * assets/custom-header-drawer.js: "si ningun tab esta activo, activar el primero").
 *
 * Solo hace show/hide (instant swap, sin animacion - decision del cliente). Cada panel ya trae
 * su propio <slideshow-component> nativo independiente (snippets/resource-list.liquid) - este
 * componente no toca el carrusel, solo alterna que panel esta visible.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 *
 * @extends {Component<Refs>}
 */
export class CustomCarouselTabs extends Component {
  requiredRefs = ['tablist'];

  connectedCallback() {
    super.connectedCallback();
    this.#buildTabs();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  get #tabs() {
    return Array.from(this.refs.tablist.querySelectorAll('[role="tab"]'));
  }

  get #panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]')).slice(0, 4);
  }

  #buildTabs() {
    this.refs.tablist.innerHTML = '';
    const panels = this.#panels;

    panels.forEach((panel, index) => {
      const tabId = panel.id.replace('carousel-panel-', 'carousel-tab-');

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.id = tabId;
      tab.setAttribute('aria-controls', panel.id);
      tab.className = 'carousel-tabs__tab';
      tab.textContent = panel.dataset.tabLabel ?? '';
      tab.addEventListener('click', () => this.#activateTab(tab));

      panel.setAttribute('aria-labelledby', tabId);

      this.refs.tablist.append(tab);

      if (index < panels.length - 1) {
        const divider = document.createElement('span');
        divider.className = 'carousel-tabs__divider';
        divider.setAttribute('aria-hidden', 'true');
        this.refs.tablist.append(divider);
      }
    });

    const [firstTab] = this.#tabs;
    if (firstTab) this.#activateTab(firstTab);
  }

  /**
   * @param {HTMLElement} tab
   */
  #activateTab(tab) {
    const panelId = tab.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;

    for (const candidate of this.#tabs) {
      const active = candidate === tab;
      candidate.setAttribute('aria-selected', String(active));
      candidate.setAttribute('tabindex', active ? '0' : '-1');
    }

    for (const candidate of this.#panels) {
      candidate.toggleAttribute('hidden', candidate !== panel);
    }

    const collectionUrl = panel?.dataset.collectionUrl;
    if (collectionUrl) {
      for (const link of this.querySelectorAll('.carousel-tabs__cta a')) {
        link.setAttribute('href', collectionUrl);
      }
    }
  }

  /**
   * Navegacion por flechas entre los tabs (foco en el tab bar), Home/End al primero/ultimo.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const tabs = this.#tabs;
    const index = tabs.indexOf(target);
    if (index === -1) return;

    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (event.key === 'Home') next = tabs[0];
    if (event.key === 'End') next = tabs[tabs.length - 1];

    if (next instanceof HTMLElement) {
      event.preventDefault();
      next.focus();
      this.#activateTab(next);
    }
  };
}

if (!customElements.get('custom-carousel-tabs')) {
  customElements.define('custom-carousel-tabs', CustomCarouselTabs);
}
